import { useEffect, useRef, useState } from "react";
import { useDebug } from '@/utils/useDebugLog';
import { AnimVals } from '@/utils/animUtils';

import gsap from 'gsap';
import SplitText from 'gsap/SplitText';

import className from "classnames/bind";
import styles from "./HeadlineAnim.module.scss";
const cx = className.bind(styles);

/* Component description
 * This component animates a headline by splitting it into characters and animating them in sequentially.
 * When the animation completes, the duplicates are removed from the DOM to save resources. */

interface HeadlineAnimProps {
  tag?: React.ElementType;
  text: string;
  className?: string | string[];
  isCenterAlign?: boolean;
  isRevealEnabled?: boolean;
  isInView?: boolean;
  animInDelay?: number;
  onAnimationStart?: () => void;
  onAnimationComplete?: () => void;
  maxCharsPerWord?: number;
}

export default function HeadlineAnim({
  tag: Tag = "h1",
  text,
  className,
  isCenterAlign = false,
  isRevealEnabled = true,
  isInView = false,
  animInDelay = 0,
  onAnimationStart,
  onAnimationComplete,
  maxCharsPerWord,
}: HeadlineAnimProps) {

  const { log } = useDebug({ name: 'HeadlineAnim', bgColor: 'rebeccapurple', textColor: 'white', enabled: false });

  const DEBUGMODE: boolean = false;

  const ANIM = AnimVals.HeadlineAnim;

  // Check if any word exceeds maxCharsPerWord limit
  const checkWordsForMaxChars = () => {
    if (!maxCharsPerWord) return false;

    // Strip HTML tags to get plain text for word length checking
    const plainText = text?.replace(/<[^>]*>/g, '');
    const words = plainText.split(/\s+/);

    return words.some(word => word.length > maxCharsPerWord);
  };

  // Determine if animation should be enabled based on maxCharsPerWord check
  const animationEnabled = isRevealEnabled && !checkWordsForMaxChars();

  // States
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAnimatingComplete, setIsAnimatingComplete] = useState(false);

  // Elements
  const rootEl = useRef<HTMLDivElement>(null);
  const animRef = useRef<HTMLElement>(null);

  // -------------------- Animation

  useEffect(() => {
    if (!animationEnabled || !isInView) return;
    if (!animRef.current) return;

    // Store split and timeline refs for cleanup
    let split: SplitText | null = null;
    let tl: gsap.core.Timeline | null = null;

    // Wrap hyphens in span tags before splitting
    // This prevents SplitText from breaking up hyphenated words incorrectly
    // We need to preserve existing HTML structure to respect highlight markup
    const wrapBareHyphens = (element: HTMLElement) => {
      // Process each child node
      element.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Only process text nodes, wrap bare hyphens
          const text = node.textContent || '';
          if (text.includes('-')) {
            const wrappedHTML = text?.replace(/-/g, '<span>-</span>');
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = wrappedHTML;

            // Replace the text node with the new nodes
            const fragment = document.createDocumentFragment();
            Array.from(tempDiv.childNodes).forEach(child => fragment.appendChild(child));
            node.parentNode?.replaceChild(fragment, node);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Recursively process element nodes
          wrapBareHyphens(node as HTMLElement);
        }
      });
    };

    wrapBareHyphens(animRef.current);

    // Split
    split = new SplitText(animRef.current, {
      type: 'lines, words, chars',
      linesClass: 'gline'
    });

    // If there are multiple lines the character distance needs to be increased
    let charDistance = ANIM.charDistance;
    if (split.lines.length > 1) {
      const numericValue = parseFloat(charDistance?.toString().replace('%', ''));
      charDistance = `${numericValue * 1.5}%`;
    }

    // Timeline
    tl = gsap.timeline({
      onStart: () => {
        setIsAnimating(true);
        if (onAnimationStart) onAnimationStart();
      },
      onComplete: () => {
        if (DEBUGMODE) return;
        setIsAnimatingComplete(true);
        if (onAnimationComplete) onAnimationComplete();
        // Note: Do not call split.revert() here to avoid visual hitch -Gabe
      },
      delay: animInDelay,
    }).from(split.chars, {
      opacity: ANIM.charOpacity,
      y: charDistance, // ANIM.charDistance,
      duration: ANIM.duration,
      ease: ANIM.ease,
      stagger: ANIM.stagger
    });

    // tl.timeScale(0.25); // Debug only

    // Cleanup function
    return () => {
      if (tl) tl.kill();
      if (split) split.revert();
    };

  }, [animationEnabled, isInView]);

  // -------------------- Render

  return (
    animationEnabled

    // Mode: Animation
    ? (
      <div ref={rootEl}
        className={cx("headline-anim", { centered: isCenterAlign })}
        style={{ visibility: (animationEnabled && !isAnimating) ? 'hidden' : 'visible' }}
      >
        <Tag
          className={cx([
            "base",
            className,
            { hidden: animationEnabled && isInView && !isAnimatingComplete }
          ])}
          dangerouslySetInnerHTML={{ __html: text }}
        />

        {animationEnabled && !isAnimatingComplete && (
          <Tag className={cx([
              "anim",
              className,
              { hidden: !isInView }
            ])}
            ref={animRef}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        )}
      </div>
    )

    // Mode: No animation
    : (
      <Tag className={cx(["base", className])} dangerouslySetInnerHTML={{ __html: text }} />
    )
  );

}
